import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/customSupabaseClient';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SupportAssignmentControl = ({ userId }) => {
  const [staff, setStaff] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [staffRes, assignedRes] = await Promise.all([
      supabase.rpc('admin_list_support_staff'),
      supabase.rpc('admin_get_user_support_assignments', { p_user_id: userId }),
    ]);
    if (staffRes.error || assignedRes.error) {
      console.error('Error loading support assignments:', staffRes.error || assignedRes.error);
      return;
    }
    setStaff(staffRes.data || []);
    setAssigned(assignedRes.data || []);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const mutate = async (rpc, staffId, successMessage) => {
    setSaving(true);
    const { error } = await supabase.rpc(rpc, { p_staff_id: staffId, p_user_id: userId });
    setSaving(false);
    if (error) {
      toast.error('Could not update support assignment', { description: error.message });
      return;
    }
    toast.success(successMessage);
    load();
  };

  const assignedIds = new Set(assigned.map((a) => a.staff_id));
  // Staff can also be athletes; a staff member must never be assigned to themselves.
  const available = staff.filter((s) => s.id !== userId && !assignedIds.has(s.id));

  if (staff.length === 0 && assigned.length === 0) return null;

  return (
    <div className="flex flex-col items-center sm:items-start gap-1 mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Support staff</span>
        {assigned.map((a) => (
          <Badge key={a.staff_id} variant="secondary" className="gap-1">
            {a.full_name || 'Unnamed'}
            <button
              type="button"
              aria-label="Remove support assignment"
              disabled={saving}
              onClick={() => mutate('admin_unassign_support_user', a.staff_id, 'Support assignment removed')}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {available.length > 0 && (
          <Select
            disabled={saving}
            value=""
            onValueChange={(staffId) => mutate('admin_assign_support_user', staffId, 'User assigned to support')}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue placeholder="Assign support..." />
            </SelectTrigger>
            <SelectContent>
              {available.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.full_name || s.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Support staff only see users assigned here.
      </p>
    </div>
  );
};

export default SupportAssignmentControl;
